import intl from 'react-intl-universal';
import download from 'downloadjs';
import mime from 'mime-types';
import userRolesConfig from './userRolesConfig';
import allFalse from './allFalse';
import throttle from './throttle';
import capitalize from './capitalize';
import getKeyOfValue from './getKeyOfValue';
import transformFormValues from './transformFormValues';
import getMessageId from './getMessageId';
import cleanArray from './cleanArray';
import urlParamsMapper from './urlParamsMapper';

function updateBodyClass(name) {
  const bodyClass = name.replace(/[/]/gi, '').toLowerCase();
  document.body.classList.toggle(bodyClass);
}

function updateBodyClassState(name, state) {
  const bodyClass = name.replace(/[/]/gi, '').toLowerCase();
  document.body.classList.toggle(bodyClass, state);
}

function getAPIUrls(host = '', paths = {}, overrides = {}) {
  return Object.entries(paths).reduce((acc, [key, val]) => {
    return { ...acc, [key]: overrides[key] ? overrides[key] : host + val };
  }, {});
}

function createTableColumns(type, config) {
  if (config.columns === undefined) {
    throw Error('createTableColumns requires { columns: [] } for config argument');
  }

  /* if no visible order is provided then assume all values in `key` will be visible unless overriden from options */
  const visibleKeys = config.visible || config.columns;
  const visibleCols = visibleKeys.reduce((acc, next) => {
    acc[next] = true;
    return acc;
  }, {});

  const cols = config.columns.map(col => ({
    id: col,
    displayText: intl.get(`${type}.headers.${col}`),
    sortable: true,
    visible: visibleCols[col] || false,
    ...(config.options && config.options[col] ? config.options[col] : {}),
  }));
  cols[0].stickyLeft = true;

  return cols;
}

function createTableRows(columns, data) {
  const rows = data.map(row => {
    return columns.map(column => {
      return row[column.id] !== undefined && row[column.id] !== null ? row[column.id].toString() : '';
    });
  });

  return rows;
}

function createTableData(type, config, check = false) {
  if (config.columns === undefined || config.rows === undefined) {
    throw Error('createTableData requires { columns: [], rows: [] } for config argument');
  }

  /* if no visible order is provided then assume all values in `key` will be visible unless overriden from options */
  const visibleKeys = config.visible || config.columns;
  const visibleCols = visibleKeys.reduce((acc, next) => {
    acc[next] = true;
    return acc;
  }, {});

  const cols = config.columns.map(col => ({
    id: col,
    displayText: intl.get(`${type}.headers.${col}`),
    sortable: true,
    visible: visibleCols[col] || false,
    ...(config.options && config.options[col] ? config.options[col] : {}),
  }));
  !check && (cols[0].stickyLeft = true); // to switch the default sticky left column

  const rows = config.rows.map(row => {
    return cols.map(column => {
      return row[column.id] !== undefined && row[column.id] !== null ? row[column.id].toString() : '';
    });
  });

  return {
    columns: cols,
    rows,
  };
}

function createRowMap(columns, row) {
  const rowMap = columns.reduce((acc, next, index) => {
    acc[next.id] = row[index].value;
    return acc;
  }, {});

  return rowMap;
}

function checkFields(source, fields) {
  const isFieldsFilled = fields.reduce((acc, next) => {
    if (source[next] === '' || source[next] === undefined || source[next] === null || source[next] === -1) {
      acc = false;
    }
    return acc;
  }, true);

  return isFieldsFilled;
}

function selectItemInArrays(index, data) {
  let flattened;
  if (Array.isArray(data)) {
    flattened = data;
    return flattened[index];
  }
}

function saveAPIFile(attachmentData, fileName) {
  if (fileName) {
    fileName = fileName.split('\\').pop();
  }
  download(atob(attachmentData), fileName, mime.lookup(fileName));
}

function transformToVals(data) {
  return data;
}

function transformCaseVals(data) {
  return {
    firstName: data.firstName,
    lastName: data.lastName,
    engagement: data.engagement,
    caseId: data.caseId,
    caseName: data.caseName,
  };
}

function getToProperty(arr) {
  if (arr.length > 1) {
    if (arr.length > 3) {
      arr.length = 3;
    }
    let ref = arr.map((each, i) => {
      // limit returned results to only first 3
      return each.name + (each.company ? ', ' + each.company : '');
    });
    return ref.join('; ');
  } else {
    return arr[0] && arr[0].name + (arr[0].company ? ', ' + arr[0].company : '');
  }
}

function convertModelToFormData(data = {}, form = null, namespace = '') {
  let files = {};
  let model = {};
  for (let propertyName in data) {
    if (data[propertyName] && data[propertyName] instanceof File) {
      files[propertyName] = data[propertyName];
    } else if (data[propertyName] && data[propertyName].length && data[propertyName][0] instanceof File) {
      files[propertyName] = [];
      data[propertyName].forEach(file => {
        files[propertyName].push(file);
      });
    } else {
      model[propertyName] = data[propertyName];
    }
  }

  model = JSON.parse(JSON.stringify(model));
  let formData = form || new FormData();

  for (let propertyName in model) {
    if (!model.hasOwnProperty(propertyName) || !model[propertyName]) continue;
    let formKey = namespace ? `${namespace}[${propertyName}]` : propertyName;
    if (model[propertyName] instanceof Date) formData.append(formKey, model[propertyName].toISOString());
    else if (model[propertyName] instanceof File) {
      formData.append(formKey, model[propertyName], model[propertyName].fileName);
    } else if (model[propertyName] instanceof Array) {
      model[propertyName].forEach((element, index) => {
        const tempFormKey = `${formKey}[${index}]`;
        if (typeof element === 'object' && !(element instanceof File)) convertModelToFormData(element, formData, tempFormKey);
        else formData.append(tempFormKey, element.toString());
      });
    } else if (typeof model[propertyName] === 'object' && !(model[propertyName] instanceof File))
      convertModelToFormData(model[propertyName], formData, formKey);
    else {
      formData.append(formKey, model[propertyName].toString());
    }
  }

  // Files must come in array form initially, but appended to the form individually to the same propertyName
  for (let propertyName in files) {
    if (files.hasOwnProperty(propertyName)) {
      for (let index in files[propertyName]) {
        formData.append(propertyName, files[propertyName][index]);
      }
    }
  }
  return formData;
}

function generateColorLuminance(hex, lum) {
  hex = String(hex).replace(/[^0-9a-f]/gi, '');
  if (hex.length < 6) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  lum = lum || 0;

  var rgb = '#',
    c,
    i;
  for (i = 0; i < 3; i++) {
    c = parseInt(hex.substr(i * 2, 2), 16);
    c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16);
    rgb += ('00' + c).substr(c.length);
  }

  return rgb;
}

function getEYColors(numOfColors) {
  const eyColors = [
    '#87D3F2',
    '#4EBEEB',
    '#35A4E8',
    '#188CE5',
    '#1777CF',
    '#155CB4',
    '#082C65',
    '#3D108A',
    '#240D75',
    '#542EA5',
    '#724BC3',
    '#1777CF',
    '#35A4E8',
    '#4EBEEB',
  ];
  const numOfEYColors = eyColors.length;
  let eyColorIndex = 0;

  const colors = [];
  let i = 0;

  for (i; i < numOfColors; i += 1) {
    colors.push(eyColors[eyColorIndex]);
    eyColorIndex = eyColorIndex + 1 === numOfEYColors ? 0 : eyColorIndex + 1;
  }

  return colors;
}

function applyThemeClassName() {
  setTimeout(() => {
    const currentBodyBgColor = window.getComputedStyle(document.body, null).getPropertyValue('background-color');
    if (currentBodyBgColor === 'rgb(255, 255, 255)') {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    } else {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    }
  }, 300);
}

function formatMultiPartLabel(text) {
  if (!text) return text;
  const labelParts = text.split('|');
  const label = labelParts.length > 0 ? labelParts.shift() : '';
  const labelDetail = labelParts.join(' | ');
  const labelText = labelDetail ? `${label} (${labelDetail})` : label;
  return labelText;
}

export {
  allFalse,
  applyThemeClassName,
  capitalize,
  cleanArray,
  checkFields,
  throttle,
  createTableColumns,
  createTableRows,
  createTableData,
  createRowMap,
  formatMultiPartLabel,
  getEYColors,
  getAPIUrls,
  getKeyOfValue,
  getMessageId,
  updateBodyClass,
  updateBodyClassState,
  transformFormValues,
  selectItemInArrays,
  saveAPIFile,
  userRolesConfig,
  transformToVals,
  transformCaseVals,
  urlParamsMapper,
  getToProperty,
  convertModelToFormData,
  generateColorLuminance,
};
